/**
 * @fileoverview Backtracking line search with the Armijo (sufficient-decrease)
 * condition.
 *
 * Given a current point `x`, its energy `f0` and gradient `grad`, and a
 * descent `direction` (typically `-H.g` from L-BFGS, or `-g` for steepest
 * descent), this finds a step length alpha along `direction` that produces a
 * sufficient decrease in energy:
 *
 *     f(x + alpha*d) <= f0 + c1 * alpha * (grad . d)
 *
 * (the Armijo / sufficient-decrease condition). The search starts at
 * alpha = 1, contracting by beta each time the condition fails, for up to
 * `maxBacktracks` attempts. If no acceptable step is found — or if
 * `direction` is not a descent direction (grad . d >= 0) — it returns
 * `{ ok: false }` so the driver can reset L-BFGS and retry with steepest
 * descent.
 *
 * Hot-path discipline: the trial point is written into a caller-supplied or
 * internally-cached scratch buffer; no per-backtrack allocation occurs. The
 * returned `xNew` aliases that scratch buffer (a `Float64Array` of length n) —
 * the driver should copy/consume it before the next line-search call if it
 * needs to retain it.
 *
 * The `evalFn` interface the `minimize.js` driver codes to:
 *
 *     evalFn(coords: Float64Array) -> { energy: number, gradient: Float64Array }
 *
 *   - `coords` is a length-n flat `Float64Array` (3 * nAtoms in engine use,
 *     units nm). The line search passes its trial-point scratch buffer here;
 *     `evalFn` must not retain or mutate `coords`.
 *   - returns `energy` in kJ/mol and `gradient` (length n, kJ/mol/nm). The
 *     line search only reads `energy`; `gradient` is returned to the caller
 *     so the driver can reuse the evaluation at the accepted point without a
 *     redundant call. `evalFn` may return the same gradient buffer on each
 *     call (the driver is responsible for copying if needed).
 */

/**
 * @typedef {Object} EvalResult
 * @property {number} energy          Potential energy at the evaluated point, kJ/mol.
 * @property {Float64Array} gradient  Gradient at the evaluated point, length n, kJ/mol/nm.
 */

/**
 * @callback EvalFn
 * @param {Float64Array} coords   Trial coordinates, length n (nm). Must not be retained or mutated.
 * @returns {EvalResult}
 */

/**
 * @typedef {Object} LineSearchOptions
 * @property {number}  [alpha0=1]          Initial step length.
 * @property {number}  [beta=0.5]          Contraction factor (0 < beta < 1).
 * @property {number}  [c1=1e-4]           Armijo sufficient-decrease constant (0 < c1 < 1).
 * @property {number}  [maxBacktracks=20]  Max number of trial steps before giving up.
 * @property {Float64Array} [scratch]      Optional length-n scratch for the trial point.
 *                                         If absent, an internal buffer is used.
 */

/**
 * @typedef {Object} LineSearchResult
 * @property {boolean}      ok       True if a step satisfying Armijo was found.
 * @property {number}       [alpha]  Accepted step length (present iff ok).
 * @property {number}       [fNew]   Energy at the accepted point (present iff ok).
 * @property {Float64Array} [xNew]   Accepted point, length n. Aliases the scratch
 *                                   buffer (present iff ok).
 * @property {Float64Array} [gradNew] Gradient at the accepted point, length n
 *                                    (present iff ok). Whatever `evalFn` returned there.
 */

/**
 * Module-level cache of internal scratch buffers, keyed by length. Avoids
 * reallocation across calls when the caller does not supply its own scratch.
 *
 * @type {Map<number, Float64Array>}
 */
const scratchCache = new Map();

/**
 * @param {number} n
 * @returns {Float64Array}
 */
function getScratch(n) {
    let buf = scratchCache.get(n);
    if (!buf) {
        buf = new Float64Array(n);
        scratchCache.set(n, buf);
    }
    return buf;
}

/**
 * Backtracking line search satisfying the Armijo sufficient-decrease
 * condition.
 *
 * @param {EvalFn} evalFn        Energy/gradient evaluator (see file header for the contract).
 * @param {Float64Array} x       Current point, length n (nm).
 * @param {number} f0            Energy at `x`, kJ/mol.
 * @param {Float64Array} grad    Gradient at `x`, length n, kJ/mol/nm.
 * @param {Float64Array} direction   Search direction d, length n.
 * @param {LineSearchOptions} [opts]
 * @returns {LineSearchResult}
 */
export function backtrackingLineSearch(evalFn, x, f0, grad, direction, opts = {}) {
    const n = x.length;
    const alpha0 = opts.alpha0 !== undefined ? opts.alpha0 : 1;
    const beta = opts.beta !== undefined ? opts.beta : 0.5;
    const c1 = opts.c1 !== undefined ? opts.c1 : 1e-4;
    const maxBacktracks = opts.maxBacktracks !== undefined ? opts.maxBacktracks : 20;
    const scratch = opts.scratch || getScratch(n);

    // Directional derivative g . d. For a valid descent direction this is
    // strictly negative. If it is >= 0, no step along `direction` can
    // guarantee decrease — bail so the driver can fall back.
    let gd = 0;
    for (let i = 0; i < n; i++) gd += grad[i] * direction[i];

    if (!(gd < 0)) {
        return { ok: false };
    }

    let alpha = alpha0;
    for (let k = 0; k < maxBacktracks; k++) {
        // Trial point x + alpha*d into the scratch buffer (no allocation).
        for (let i = 0; i < n; i++) scratch[i] = x[i] + alpha * direction[i];

        const { energy: fNew, gradient: gradNew } = evalFn(scratch);

        // Armijo sufficient-decrease test. Guard against NaN/Inf energies
        // (a trial point may land in a singular configuration): treat them
        // as a failure and keep contracting.
        if (Number.isFinite(fNew) && fNew <= f0 + c1 * alpha * gd) {
            return {
                ok: true,
                alpha,
                fNew,
                xNew: scratch,
                gradNew,
            };
        }

        alpha *= beta;
    }

    return { ok: false };
}

/**
 * @typedef {Object} WolfeLineSearchOptions
 * @property {number}  [alpha0=1]          Initial step length.
 * @property {number}  [alphaMax=10]       Cap on bracket expansion.
 * @property {number}  [expand=2]          Expansion factor (alpha *= expand) until bracket.
 * @property {number}  [c1=1e-4]           Armijo sufficient-decrease constant.
 * @property {number}  [c2=0.9]            Strong Wolfe curvature constant (c1 < c2 < 1).
 *                                         0.9 is the canonical choice for L-BFGS.
 * @property {number}  [maxIterations=25]  Combined bracket + zoom iteration cap.
 * @property {number}  [bracketTol=1e-9]   Stop zoom when |alpha_hi - alpha_lo| < this.
 * @property {Float64Array} [scratch]      Optional length-n scratch for the trial point.
 */

/**
 * Strong Wolfe line search (Nocedal-Wright Algorithm 3.5).
 *
 * Finds a step length alpha along `direction` satisfying both:
 *
 *     (Armijo)  f(x + alpha*d) <= f0 + c1 * alpha * (grad0 . d)
 *     (Strong   |grad(x + alpha*d) . d| <= c2 * |grad0 . d|
 *      Wolfe)
 *
 * The curvature condition guarantees positive (s, y) inner product in the
 * L-BFGS update, which the Armijo-only backtracking does not. This typically
 * lets the driver run with a much smaller history size (m ~ 6-10 instead of
 * m=50) at comparable convergence quality.
 *
 * Cost: one force evaluation per trial point. The curvature check uses
 * `grad(x + alpha*d) . d`, extracted from the gradient that `evalFn` already
 * returns -- no extra evaluations beyond what Armijo does (Wolfe's per-iter
 * cost is dominated by the count of trial points it visits, not curvature
 * checks).
 *
 * Two-phase structure:
 *   Phase 1 (bracketing): start at alpha = alpha0, expand by `expand` until
 *     one of three brackets a minimizer:
 *       a) Armijo violated at alpha (or alpha increased the energy compared to
 *          alpha_prev): zoom in (alpha_prev, alpha).
 *       b) Curvature condition is met at alpha: accept alpha.
 *       c) Directional derivative becomes non-negative at alpha (we've
 *          overshot a minimizer): zoom in (alpha, alpha_prev) -- note the
 *          reversed order; the lower-phi endpoint is alpha.
 *   Phase 2 (zoom): bisect the bracket until both conditions hold or the
 *     bracket collapses below `bracketTol`.
 *
 * Failure modes (returns `{ ok: false }`):
 *   - direction is not a descent direction (grad0 . d >= 0)
 *   - max iterations exceeded without satisfying both conditions
 *   - bracket collapses without finding a Wolfe point
 *
 * On failure the caller should fall back to backtrackingLineSearch + a steepest
 * descent reset (mirror of the existing Armijo fallback in minimize.js).
 *
 * @param {EvalFn} evalFn        Energy/gradient evaluator (see file header).
 * @param {Float64Array} x       Current point, length n (nm).
 * @param {number} f0            Energy at `x`, kJ/mol.
 * @param {Float64Array} grad    Gradient at `x`, length n, kJ/mol/nm.
 * @param {Float64Array} direction   Search direction d, length n.
 * @param {WolfeLineSearchOptions} [opts]
 * @returns {LineSearchResult}
 */
export function strongWolfeLineSearch(evalFn, x, f0, grad, direction, opts = {}) {
    const n = x.length;
    const alpha0 = opts.alpha0 !== undefined ? opts.alpha0 : 1;
    const alphaMax = opts.alphaMax !== undefined ? opts.alphaMax : 10;
    const expand = opts.expand !== undefined ? opts.expand : 2;
    const c1 = opts.c1 !== undefined ? opts.c1 : 1e-4;
    const c2 = opts.c2 !== undefined ? opts.c2 : 0.9;
    const maxIterations = opts.maxIterations !== undefined ? opts.maxIterations : 25;
    const bracketTol = opts.bracketTol !== undefined ? opts.bracketTol : 1e-9;
    const scratch = opts.scratch || getScratch(n);

    // Directional derivative at alpha = 0: g0 . d. For a descent direction
    // this is strictly negative; otherwise bail so the driver can fall back.
    let gd0 = 0;
    for (let i = 0; i < n; i++) gd0 += grad[i] * direction[i];
    if (!(gd0 < 0)) return { ok: false };

    /**
     * Evaluate phi(alpha) = f(x + alpha*d) and phi'(alpha) = grad(...) . d.
     * Returns { f, gd, grad } with the gradient buffer aliasing whatever
     * evalFn returned (the caller copies if it needs to retain it).
     *
     * @param {number} alpha
     */
    function evalAt(alpha) {
        for (let i = 0; i < n; i++) scratch[i] = x[i] + alpha * direction[i];
        const { energy: fNew, gradient: gradNew } = evalFn(scratch);
        let gdNew = 0;
        for (let i = 0; i < n; i++) gdNew += gradNew[i] * direction[i];
        return { f: fNew, gd: gdNew, gradNew };
    }

    /**
     * Zoom phase: bisect [alphaLo, alphaHi] (alphaLo is the better endpoint:
     * lower phi(alpha) and its directional derivative points toward the other
     * endpoint) until both Wolfe conditions hold or the bracket collapses.
     *
     * @param {number} alphaLo
     * @param {number} fLo       phi(alphaLo)
     * @param {number} alphaHi
     * @param {number} budget    Remaining iterations
     */
    function zoom(alphaLo, fLo, alphaHi, budget) {
        let lo = alphaLo;
        let hi = alphaHi;
        let fL = fLo;
        for (let k = 0; k < budget; k++) {
            if (Math.abs(hi - lo) < bracketTol) return { ok: false };
            // Simple bisection. (Quadratic / cubic interpolation typically saves
            // ~30% of zoom iters in practice but adds 20 lines of code; the
            // bracket usually collapses in 2-5 iters anyway, so bisection is
            // good enough at this scope.)
            const alphaTrial = 0.5 * (lo + hi);
            const { f: fT, gd: gdT, gradNew } = evalAt(alphaTrial);
            // Treat NaN/Inf as Armijo failure (energy went to infinity).
            const armijoFail = !Number.isFinite(fT) || fT > f0 + c1 * alphaTrial * gd0;
            if (armijoFail || fT >= fL) {
                hi = alphaTrial;
                continue;
            }
            if (Math.abs(gdT) <= -c2 * gd0) {
                // Both conditions met.
                return {
                    ok: true, alpha: alphaTrial, fNew: fT, xNew: scratch, gradNew,
                };
            }
            // Curvature condition not yet met; tighten the bracket.
            if (gdT * (hi - lo) >= 0) hi = lo;
            lo = alphaTrial;
            fL = fT;
        }
        return { ok: false };
    }

    // Phase 1: bracketing. Track (alpha_prev, f_prev) for the case where
    // phi(alpha) increases over phi(alpha_prev) -- the minimizer is then
    // bracketed by (alpha_prev, alpha) and zoom starts with alpha_prev as lo.
    let alphaPrev = 0;
    let fPrev = f0;
    let alpha = alpha0;

    for (let k = 0; k < maxIterations; k++) {
        const { f: fA, gd: gdA, gradNew } = evalAt(alpha);
        const armijoFail = !Number.isFinite(fA) || fA > f0 + c1 * alpha * gd0;
        const energyIncreasedFromPrev = k > 0 && fA >= fPrev;
        if (armijoFail || energyIncreasedFromPrev) {
            return zoom(alphaPrev, fPrev, alpha, maxIterations - k);
        }
        if (Math.abs(gdA) <= -c2 * gd0) {
            // Both conditions met without needing the zoom phase.
            return {
                ok: true, alpha, fNew: fA, xNew: scratch, gradNew,
            };
        }
        if (gdA >= 0) {
            // Overshot the minimizer; zoom with the lower-phi endpoint (alpha)
            // as lo and alpha_prev as hi (note the reversed order).
            return zoom(alpha, fA, alphaPrev, maxIterations - k);
        }
        // Expand alpha and keep going. Cap at alphaMax.
        alphaPrev = alpha;
        fPrev = fA;
        alpha = Math.min(alpha * expand, alphaMax);
        if (alphaPrev === alpha) {
            // Hit the cap without bracketing -- bail (driver falls back).
            return { ok: false };
        }
    }

    return { ok: false };
}
